class QuickView extends HTMLElement {
  constructor() {
    super();
    this.isListenerAdded = false;
    this.init();
    this.productPageCounter = 0;
  }

  init() {
    if (!window.__quickViewInitDone) {
      window.__quickViewInitDone = true;

      window.__quickViewClickHandler = (e) => {
        const inst = document.querySelector('quick-view');
        if (inst && inst.handleClick) inst.handleClick(e);
      };
      document.addEventListener("click", window.__quickViewClickHandler, false);

      window.__quickViewObserver = new MutationObserver((mutations) => {
        const inst = document.querySelector('quick-view');
        if (!inst) return;

        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1 && node.matches(".js-open-quickview")) {
              node.replaceWith(node.cloneNode(true));
              const fresh = document.querySelectorAll(".js-open-quickview");
              fresh.forEach(btn => {
                if (!btn.__qvBound) {
                  btn.addEventListener("click", (event) => {
                    event.preventDefault();
                    btn.innerHTML = btn.getAttribute("data-loading-text");
                    const url = btn.dataset.url;
                    if (url) inst.loadContent(url);
                  });
                  btn.__qvBound = true;
                }
              });
            }
          });
        });
      });
      window.__quickViewObserver.observe(document.body, { childList: true, subtree: true });
    }
  }

  async loadContent(url) {
    try {
      const quickView = document.querySelector("quick-view");
      if (quickView) {
        while (quickView.firstChild) {
          quickView.removeChild(quickView.firstChild);
        }
      }

      const response = await fetch(url);
      if (!response.ok) throw new Error("Network error");
      const text = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, "text/html");
      const productPage = doc.querySelector('.js-product').parentElement;

      if (productPage) {
        await this.loadQuickViewAssets();

        const productId = productPage.querySelector(".js-product").getAttribute("data-product-id");
        const productJsonElement = doc.getElementById(`ProductJson${productId}`);
        let data = {};

        if (productJsonElement) {
          data = JSON.parse(productJsonElement.innerHTML);
          const jsonScript = document.createElement("script");
          jsonScript.type = "application/json";
          jsonScript.id = `ProductJson${productId}`;
          jsonScript.textContent = JSON.stringify(data);
          document.body.appendChild(jsonScript);
        } else {
          console.error("Product JSON element not found");
        }

        let componentName;

        this.productPageCounter++;
        componentName = `product-page-${this.productPageCounter}`;

        const productInstance = document.createElement(componentName);

        if (productPage) {
          const subscriptionScripts = Array.from(productPage.querySelectorAll('script')).filter(script =>
            (script.src && script.src.includes('subscription')) ||
            (script.textContent && script.textContent.includes('subscription'))
          );

          subscriptionScripts.forEach(script => script.remove());

          productInstance.appendChild(productPage);

          subscriptionScripts.forEach((script, index) => {
            setTimeout(() => {
              const newScript = document.createElement('script');
              if (script.src) {
                newScript.src = script.src;
                newScript.async = false;
                newScript.defer = false;
              } else {
                newScript.textContent = script.textContent;
              }
              document.body.appendChild(newScript);
            }, index * 100);
          });
        }

        if (quickView) {
          quickView.appendChild(productInstance);
        }

        if (!customElements.get(componentName)) {
          customElements.define(componentName, 
              class extends window.Product {}
          );
        }
        
        this.activateQuickView();
        this.initQuickViewSplide(productInstance);

        if (typeof initializeFadeInObserver === "function") {
          initializeFadeInObserver();
        }
      } else {
        console.error("Product-page element not found");
      }
    } catch (error) {
      console.error("QuickView load error:", error);
    } finally {
      setTimeout(function () {
        document.body.classList.remove("loading-quickview");
      }, 200);
    }
  }

  registerCustomElement(componentName, baseClass) {
    if (!customElements.get(componentName)) {
      customElements.define(componentName, class extends baseClass {});
    }
  }

  loadQuickViewAssets() {
    return new Promise((resolve) => {
      let assetLoadPromises = [];

      quickViewCSS.forEach((url) => {
        if (!document.querySelector(`link[href='${url}']`)) {
          let link = document.createElement("link");
          link.rel = "stylesheet";
          link.href = url;
          document.body.appendChild(link);
          assetLoadPromises.push(
            new Promise((res) => {
              link.onload = () => res();
            })
          );
        }
      });

      quickViewScripts.forEach((url) => {
        if (!document.querySelector(`script[src='${url}']`)) {
          let script = document.createElement("script");
          script.src = url;
          script.defer = true;
          document.body.appendChild(script);
          assetLoadPromises.push(
            new Promise((res) => {
              script.onload = () => res();
            })
          );
        }
      });

      Promise.all(assetLoadPromises).then(() => resolve());
    });
  }

  activateQuickView() {
    const quickViewElement = document.querySelector(".js-quickview");
    if (quickViewElement) {
      quickViewElement.classList.add("display");
      setTimeout(() => {
        quickViewElement.classList.add("active");
      }, 50);
      document.body.style.overflow = 'hidden';
      document.querySelectorAll(".js-open-quickview").forEach(btn => {
        btn.innerHTML = btn.getAttribute("data-original-text");
      });
    }
  }

  closeQuickView() {
    const quickViewElement = document.querySelector(".js-quickview");
    if (quickViewElement) {
      quickViewElement.classList.add("closing");
      setTimeout(function () {
        quickViewElement.classList.remove("active", "closing", "display");
        document.body.style.overflow = 'visible';
      }, 300);
    }
  }

  handleClick(event) {
    const target = event.target.closest(".js-open-quickview");
    if (target) {
      event.preventDefault();
      target.innerHTML = target.getAttribute("data-loading-text");
      const url = target.dataset.url;
      if (url) {
        this.loadContent(url);
      }
    }

    const closeTarget = event.target.closest(".js-close-quickview");
    if (closeTarget) {
      event.preventDefault();
      this.closeQuickView();
    }
  }

  initQuickViewSplide(root = document) {
    const imagesWrapper = root.querySelector(".js-product-page-images");
    if (!imagesWrapper || imagesWrapper.classList.contains("is-splide")) return;

    imagesWrapper.classList.add("splide", "is-splide");

    const slides = Array.from(imagesWrapper.querySelectorAll(".js-product-page-image"));
    slides.forEach(s => s.classList.remove("active"));

    const track = document.createElement("div");
    track.className = "splide__track";

    const list = document.createElement("ul");
    list.className = "splide__list";

    slides.forEach(slide => {
      const li = document.createElement("li");
      li.className = "splide__slide";

      const productImage = document.createElement("div");
      productImage.className = "product-image js-product-page-image";

      while (slide.firstChild) {
        productImage.appendChild(slide.firstChild);
      }

      li.appendChild(productImage);
      slide.remove();
      list.appendChild(li);
    });

    track.appendChild(list);
    imagesWrapper.innerHTML = "";
    imagesWrapper.appendChild(track);

    const createCustomSplideArrows = () => {
      const arrows = document.createElement("div");
      arrows.classList.add("splide__arrows");

      const prevBtn = document.createElement("button");
      prevBtn.type = "button";
      prevBtn.className = "splide__arrow splide__arrow--prev";
      prevBtn.setAttribute("aria-label", "Previous");
      prevBtn.innerHTML = `
        <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M14.5 5.5 L8.5 12 L14.5 18.5"
                fill="none"
                stroke="currentColor"
                stroke-width="1"
                stroke-linecap="round"
                stroke-linejoin="round"/>
        </svg>
      `;

      const nextBtn = document.createElement("button");
      nextBtn.type = "button";
      nextBtn.className = "splide__arrow splide__arrow--next";
      nextBtn.setAttribute("aria-label", "Next");
      nextBtn.innerHTML = `
        <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M9.5 5.5 L15.5 12 L9.5 18.5"
                fill="none"
                stroke="currentColor"
                stroke-width="1"
                stroke-linecap="round"
                stroke-linejoin="round"/>
        </svg>
      `;

      arrows.appendChild(prevBtn);
      arrows.appendChild(nextBtn);
      return arrows;
    };

    imagesWrapper.appendChild(createCustomSplideArrows());

    const splide = new Splide(imagesWrapper, {
      type: "loop",
      perPage: 1,
      perMove: 1,
      arrows: true,
      pagination: true,
      speed: 400,
      drag: true,
      rewind: true,
    });

    const thumbsContainer = root.querySelector(".js-product-page-thumbnails .thumbnails");
    if (thumbsContainer) {
      const thumbs = Array.from(thumbsContainer.querySelectorAll(".js-product-page-thumbnail"));
      const setThumbActive = (index) => {
        thumbs.forEach(t => t.classList.remove("active"));
        const t = thumbs.find(el => Number(el.dataset.index) === index + 1);
        if (t) t.classList.add("active");
      };

      thumbs.forEach((thumb) => {
        thumb.addEventListener("click", (e) => {
          e.preventDefault();
          const idx = Number(thumb.dataset.index || "1") - 1;
          splide.go(idx);
        });
      });

      splide.on("move", (newIndex) => setThumbActive(newIndex));
      setThumbActive(0);
    }

    const oldDots = root.querySelector(".js-product-image-dots");
    if (oldDots) oldDots.style.display = "none";

    splide.mount();
    imagesWrapper.__splide = splide;
  }
}

if (!customElements.get("quick-view")) {
  customElements.define("quick-view", QuickView);
}